import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Play, Upload as UploadIcon, Settings } from "lucide-react";
import { useAdmin } from "../contexts/AdminContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Opening splash screen. Single-click on "Drive into Caprio Chateau" plays the configured
 * intro video; when the video ends it routes to Center Hub.
 * Admin can replace the splash video via a small gear button (only visible in admin mode).
 */
export default function SplashScreen({ config, onEnter, onConfigUpdated }) {
    const accent = "#D4AF37";
    const { isAdmin, setGateOpen } = useAdmin();
    const [phase, setPhase] = useState("idle"); // 'idle' | 'playing' | 'admin'
    const [uploading, setUploading] = useState(false);
    const videoRef = useRef(null);
    const fileInputRef = useRef(null);

    const videoUrl = config?.splash_video_url || "";
    const title = config?.splash_title || "Drive into Caprio Chateau";

    const handleDrive = () => {
        if (videoUrl) {
            setPhase("playing");
            setTimeout(() => {
                videoRef.current?.play().catch(() => onEnter());
            }, 100);
        } else {
            onEnter();
        }
    };

    const handleVideoEnded = () => onEnter();
    const handleSkip = () => onEnter();

    const handleAdminClick = () => {
        if (isAdmin) {
            setPhase("admin");
        } else {
            setGateOpen(true);
        }
    };

    // 4-click on "THE ESTATE" heading → open admin upload (admin only).
    const titleClickRef = useRef({ count: 0, timer: null });
    const [titleClickProgress, setTitleClickProgress] = useState(0);
    const handleTitleClick = () => {
        const c = titleClickRef.current;
        if (c.timer) clearTimeout(c.timer);
        c.count += 1;
        setTitleClickProgress(c.count);
        if (c.count >= 4) {
            c.count = 0;
            setTitleClickProgress(0);
            // Either open admin upload directly (if admin) or prompt for code
            if (isAdmin) {
                setPhase("admin");
            } else {
                setGateOpen(true);
            }
            return;
        }
        c.timer = setTimeout(() => {
            c.count = 0;
            setTitleClickProgress(0);
        }, 1500);
    };

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await axios.post(`${API}/config/splash-video`, fd);
            onConfigUpdated && onConfigUpdated(res.data);
            setPhase("idle");
        } catch (err) {
            console.error("Splash upload failed", err);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    return (
        <div
            data-testid="splash-screen"
            className="fixed inset-0 z-[100] flex items-center justify-center px-6"
            style={{
                background: "radial-gradient(ellipse at center, #0c0a14 0%, #000000 70%)",
            }}
        >
            {/* Decorative pulsing rings */}
            <div
                className="absolute w-[60vmin] h-[60vmin] rounded-full pointer-events-none"
                style={{
                    border: `1px solid ${accent}22`,
                    boxShadow: `0 0 80px ${accent}22 inset, 0 0 120px ${accent}11`,
                }}
            />
            <div
                className="absolute w-[42vmin] h-[42vmin] rounded-full pointer-events-none portal-ring"
                style={{
                    background: `conic-gradient(from 0deg, transparent 0deg, ${accent}55 90deg, transparent 180deg, transparent 270deg, ${accent}33 360deg)`,
                    filter: "blur(40px)",
                    opacity: 0.6,
                }}
            />

            {phase === "idle" && (
                <div className="relative text-center fade-in z-10">
                    <div
                        className="text-[10px] tracking-[0.5em] uppercase mb-5"
                        style={{ color: accent }}
                    >
                        Caprio Chateau
                    </div>
                    <h1
                        data-testid="splash-estate-title"
                        className="font-serif-display text-6xl md:text-8xl uppercase tracking-luxe leading-none mb-3"
                        style={{
                            color: "#fff",
                            textShadow: `0 4px 40px ${accent}55, 0 1px 4px rgba(0,0,0,0.8)`,
                        }}
                    >
                        The Estate
                    </h1>
                    <p
                        className="mb-10 text-sm md:text-base font-light tracking-wide max-w-md mx-auto"
                        style={{ color: "rgba(255,255,255,0.65)" }}
                    >
                        A 360° immersive showcase. Day and night, room by room.
                    </p>

                    <button
                        type="button"
                        data-testid="splash-drive-btn"
                        onClick={handleDrive}
                        className="relative inline-flex items-center gap-3 px-8 md:px-10 py-4 rounded-full text-[11px] md:text-xs tracking-[0.4em] uppercase transition-all hover:scale-[1.04]"
                        style={{
                            background: accent,
                            color: "#0a0a0a",
                            boxShadow: `0 18px 48px -14px ${accent}, 0 0 0 1px ${accent}`,
                        }}
                    >
                        <Play size={14} />
                        {title}
                    </button>

                    <div
                        className="mt-12 text-[9px] tracking-[0.4em] uppercase"
                        style={{ color: "rgba(255,255,255,0.32)" }}
                    >
                        Press the gate to begin
                    </div>

                    {/* Visible "Enter" link for users who just want to skip in */}
                    <button
                        type="button"
                        data-testid="splash-enter-now"
                        onClick={onEnter}
                        className="mt-3 text-[9px] tracking-[0.4em] uppercase underline-offset-4 hover:underline"
                        style={{ color: accent }}
                    >
                        Or enter the chateau →
                    </button>
                </div>
            )}

            {phase === "playing" && (
                <div className="relative w-full h-full flex items-center justify-center fade-in z-10">
                    <video
                        ref={videoRef}
                        src={videoUrl}
                        controls={false}
                        playsInline
                        onEnded={handleVideoEnded}
                        onError={() => onEnter()}
                        data-testid="splash-video"
                        className="max-w-full max-h-full"
                        style={{ background: "#000" }}
                    />
                    <button
                        type="button"
                        data-testid="splash-skip"
                        onClick={handleSkip}
                        className="absolute top-6 right-6 text-[10px] tracking-[0.4em] uppercase px-4 py-2 rounded-full"
                        style={{
                            background: "rgba(0,0,0,0.5)",
                            color: accent,
                            border: `1px solid ${accent}55`,
                        }}
                    >
                        Skip →
                    </button>
                </div>
            )}

            {phase === "admin" && (
                <div className="relative z-10 max-w-md w-full fade-in">
                    <div
                        className="glass rounded-2xl p-6 text-left"
                        style={{ border: `1px solid ${accent}55` }}
                    >
                        <div
                            className="text-[10px] tracking-[0.4em] uppercase mb-3"
                            style={{ color: accent }}
                        >
                            Replace Splash Video
                        </div>
                        <p
                            className="text-xs mb-5"
                            style={{ color: "rgba(255,255,255,0.6)" }}
                        >
                            Upload an .mp4, .webm, or .mov clip. It plays after a visitor presses{" "}
                            <em>{title}</em>, then drops them into the Center Hub.
                        </p>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="video/mp4,video/webm,video/quicktime,video/x-m4v"
                            className="hidden"
                            data-testid="splash-video-input"
                            onChange={handleFile}
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            data-testid="splash-upload-btn"
                            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[11px] tracking-[0.35em] uppercase disabled:opacity-50"
                            style={{ background: accent, color: "#0a0a0a" }}
                        >
                            <UploadIcon size={14} /> {uploading ? "Uploading…" : "Choose Video"}
                        </button>
                        {videoUrl && (
                            <div className="mt-4 text-[10px] tracking-[0.2em] uppercase opacity-50" style={{ color: "#fff" }}>
                                Current: …{videoUrl.split("/").pop()}
                            </div>
                        )}
                        <button
                            type="button"
                            data-testid="splash-admin-close"
                            onClick={() => setPhase("idle")}
                            className="mt-3 w-full text-center text-[10px] tracking-[0.3em] uppercase"
                            style={{ color: "rgba(255,255,255,0.55)" }}
                        >
                            ← Back to gate
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// Refresh config after upload — used by parent.
SplashScreen.useConfig = () => {
    const [config, setConfig] = useState({ splash_video_url: "", splash_title: "Drive into Caprio Chateau" });
    useEffect(() => {
        let mounted = true;
        axios.get(`${API}/config`)
            .then((r) => { if (mounted) setConfig(r.data); })
            .catch(() => {});
        return () => { mounted = false; };
    }, []);
    return [config, setConfig];
};
